/**
 * EditDemoRequestModal Component
 * Modal for editing demo requests
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditDemoRequestModal = ({ request, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: request.title || '',
    description: request.description || '',
    label_name: request.label_name || '',
    contact_email: request.contact_email || '',
    genres: (request.genres || []).join(', '),
    deadline: request.deadline || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Request title is required');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const genresArray = formData.genres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-requests/${request.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          label_name: formData.label_name,
          contact_email: formData.contact_email,
          genres: genresArray,
          deadline: formData.deadline || null
        })
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update request');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this demo request? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-requests/${request.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        onSuccess && onSuccess();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete request');
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.6rem',
    borderRadius: '8px',
    border: '1px solid rgba(251, 191, 36, 0.3)',
    background: 'rgba(30, 20, 50, 0.8)',
    color: '#fff',
    fontSize: '0.85rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    color: '#fbbf24',
    fontSize: '0.85rem',
    marginBottom: '0.4rem'
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: '1rem'
    }}>
      <div 
        data-testid="edit-demo-request-modal"
        style={{
          background: 'linear-gradient(135deg, #1a1025 0%, #2d1f3d 50%, #1a1025 100%)',
          borderRadius: '16px',
          border: '2px solid rgba(251, 191, 36, 0.4)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '1.5rem'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#fbbf24', fontSize: '1.2rem', margin: 0 }}>
            ✏️ Edit Demo Request
          </h3>
          <button 
            onClick={onClose}
            data-testid="close-edit-request-modal" 
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Title */}
          <div>
            <label style={labelStyle}>Request Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              data-testid="request-title-input"
              style={inputStyle}
            />
          </div>

          {/* Label Name */}
          <div>
            <label style={labelStyle}>Label/Company Name</label>
            <input
              type="text"
              value={formData.label_name}
              onChange={(e) => setFormData({ ...formData, label_name: e.target.value })}
              data-testid="request-label-input"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="request-description-input"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Contact Email */}
          <div>
            <label style={labelStyle}>Contact Email</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              data-testid="request-email-input"
              style={inputStyle}
            />
          </div>

          {/* Deadline */}
          <div>
            <label style={labelStyle}>Deadline</label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              data-testid="request-deadline-input"
              style={inputStyle}
            />
          </div>

          {/* Genres */}
          <div>
            <label style={labelStyle}>Genres (comma separated)</label>
            <input
              type="text"
              value={formData.genres}
              onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
              data-testid="request-genres-input"
              style={inputStyle}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-request-btn"
              style={{
                padding: '0.65rem 1rem',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                fontSize: '0.85rem',
                fontWeight: '500',
                cursor: deleting ? 'not-allowed' : 'pointer'
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="save-request-btn"
              style={{
                flex: 1,
                padding: '0.65rem',
                borderRadius: '8px',
                border: 'none',
                background: loading ? 'rgba(251, 191, 36, 0.3)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: loading ? 'rgba(0,0,0,0.5)' : '#000',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDemoRequestModal;
